import { useMemo } from 'react';
import * as Sentry from '@sentry/react';
import { orderBy, uniqBy } from 'lodash-es';
import { subscribe, useSnapshot } from 'valtio';
import {
  fetchQuery,
  SearchHistoryArtistDocument,
  SearchHistoryCollectorDocument,
  SearchHistoryPlaylistDocument,
  SearchHistoryReleaseDocument,
} from '@graphql/reactQuery';
import type { SavedEntity } from './schema';
import { SearchHistoryStoreSchema } from './schema';
import { SearchHistoryPersistence, searchHistoryStoreState } from './store';

export const MAX_SEARCH_HISTORY_SHOW = 5;
export const MAX_SEARCH_HISTORY_SAVE = 20;

if (typeof window !== 'undefined') {
  SearchHistoryPersistence.initialValue
    .then(data => {
      if (!data) return;

      Object.assign(searchHistoryStoreState, data);

      for (const entity of data.history.slice(0, MAX_SEARCH_HISTORY_SHOW)) {
        switch (entity.type) {
          case 'Artist': {
            fetchQuery(SearchHistoryArtistDocument, {
              variables: {
                id: entity.id,
              },
              staleTime: Infinity,
            }).catch(err => {
              Sentry.captureException(err, {
                level: 'warning',
              });
            });
            continue;
          }
          case 'Release': {
            fetchQuery(SearchHistoryReleaseDocument, {
              variables: {
                id: entity.id,
              },
              staleTime: Infinity,
            }).catch(err => {
              Sentry.captureException(err, {
                level: 'warning',
              });
            });
            continue;
          }
          case 'Collector': {
            fetchQuery(SearchHistoryCollectorDocument, {
              variables: {
                id: entity.id,
              },
              staleTime: Infinity,
            }).catch(err => {
              Sentry.captureException(err, {
                level: 'warning',
              });
            });
            continue;
          }
          case 'Playlist': {
            fetchQuery(SearchHistoryPlaylistDocument, {
              variables: {
                id: entity.id,
              },
              staleTime: Infinity,
            }).catch(err => {
              Sentry.captureException(err, {
                level: 'warning',
              });
            });
            continue;
          }
          default: {
            const neverValue: never = entity.type;

            Sentry.captureMessage(`Unexpected search history entity type ${neverValue}`, 'warning');
          }
        }
      }
    })
    // eslint-disable-next-line no-console
    .catch(console.error);

  subscribe(
    searchHistoryStoreState,
    () => {
      // Debounce storage updates to minimize CPU Usage on frequent state updates
      const pendingStorageSet = setTimeout(() => {
        const parseToStore = SearchHistoryStoreSchema.safeParse(searchHistoryStoreState);
        // This way we ensure that we are inserting valid data into the storage
        // reduces sentry noise too
        if (parseToStore.success) {
          SearchHistoryPersistence.set(parseToStore.data);
        }
        clearTimeout(pendingStorageSet);
      }, 0);
    },
    true,
  );
}

export function addSearchHistory(entity: Pick<SavedEntity, 'name' | 'type' | 'id' | 'subText'>) {
  const date = Date.now();
  setTimeout(() => {
    searchHistoryStoreState.history = uniqBy(
      orderBy(
        [
          {
            ...entity,
            date,
          },
          ...searchHistoryStoreState.history,
        ],
        v => v.date,
        'desc',
      ),
      v => v.id + v.type,
    ).slice(0, MAX_SEARCH_HISTORY_SAVE);
  });
}

export function removeSearchHistory({ id }: { id: string }) {
  const index = searchHistoryStoreState.history.findIndex(entity => entity.id === id);

  if (index !== -1) {
    searchHistoryStoreState.history.splice(index, 1);
  }
}

export function clearSearchHistory() {
  searchHistoryStoreState.history = [];
}

export const useSearchHistoryStore = () => {
  const snapshot = useSnapshot(searchHistoryStoreState);

  return useMemo(() => {
    return snapshot.history.slice(0, MAX_SEARCH_HISTORY_SHOW);
  }, [snapshot]);
};
